import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projects } from './projectData';
import githubIcon from '../../resources/projects/github.png';
import './ProjectDetail.css';

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const getVideoEmbedUrl = (url: string): string | null => {
  const vimeoMatch = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);

  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  const youtubeMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/
  );

  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  return null;
};

const ProjectDetail: React.FC = () => {
  const { projectLink } = useParams<{ projectLink: string }>();
  const project = projects.find((item) => item.link === projectLink);

  if (!project) {
    return (
      <div className="pd-container">
        <h2>Project not found</h2>
        <Link to="/" className="pd-back">
          <span className="pd-back-chevron">‹</span> All Projects
        </Link>
      </div>
    );
  }

  const { detail } = project;

  const navItems = detail
    ? [
        { id: 'overview', label: 'Overview' },
        ...detail.sections.map((section) => ({
          id: slugify(section.heading),
          label: section.heading
        }))
      ]
    : [];

  const [activeId, setActiveId] = useState<string>('overview');
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    if (!detail) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    sectionRefs.current.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [project.link]);

  const registerSectionRef = (id: string) => (el: HTMLElement | null) => {
    if (el) {
      sectionRefs.current.set(id, el);
    } else {
      sectionRefs.current.delete(id);
    }
  };

  if (!detail) {
    return (
      <div className="pd-container">
        <Link to="/" className="pd-back">
          <span className="pd-back-chevron">‹</span> All Projects
        </Link>
        <div className="pd-header">
          <h1 className="pd-title">{project.title}</h1>
          <div className="pd-tags">
            {project.tags.map((tag) => (
              <span key={tag} className="pd-tag">
                {tag}
              </span>
            ))}
          </div>
        </div>
        <p className="pd-overview">{project.description}</p>
      </div>
    );
  }

  return (
    <div className="pd-layout">
      <nav className="pd-nav">
        {navItems.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={`pd-nav-link ${activeId === item.id ? 'pd-nav-link-active' : ''}`}>
            {item.label}
          </a>
        ))}
      </nav>

      <div className="pd-container">
        <Link to="/" className="pd-back">
          <span className="pd-back-chevron">‹</span> All Projects
        </Link>

        <div className="pd-header">
          <h1 className="pd-title">{project.title}</h1>

          {detail.date && <p className="pd-date">{detail.date}</p>}

          <div className="pd-tags">
            {project.tags.map((tag) => (
              <span key={tag} className="pd-tag">
                {tag}
              </span>
            ))}

            {detail.githubLinks && detail.githubLinks.length > 0 && (
              <span className="pd-tags-divider" aria-hidden="true" />
            )}

            {detail.githubLinks &&
              detail.githubLinks.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pd-tag pd-github-pill">
                  <img src={githubIcon} alt="" className="pd-github-icon" />
                  {link.label}
                </a>
              ))}
          </div>
        </div>

        {detail.images && detail.images.length > 0 ? (
          <div className="pd-triptych">
            {detail.images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`${project.title} render ${i + 1}`}
                className="pd-triptych-img"
              />
            ))}
          </div>
        ) : detail.gifUrl ? (
          <div className="pd-video-wrapper">
            <img className="pd-video pd-gif" src={detail.gifUrl} alt={`${project.title} preview`} />
          </div>
        ) : (
          detail.videoUrl &&
          (() => {
            const embedUrl = getVideoEmbedUrl(detail.videoUrl);

            return embedUrl ? (
              <div className="pd-video-wrapper">
                <iframe
                  className="pd-video"
                  src={embedUrl}
                  title={`${project.title} video`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : null;
          })()
        )}

        {detail.note && <p className="pd-note">{detail.note}</p>}

        <div id="overview" ref={registerSectionRef('overview')} className="pd-overview-section">
          <h2 className="pd-section-label">Overview</h2>
          <div className="pd-overview">
            {detail.overview.split('\n\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>

        {detail.sections.length > 0 && (
          <div className="pd-technical">
            <h2 className="pd-section-label">Technical Details</h2>
            <div className="pd-sections">
              {detail.sections.map((section) => {
                const sectionId = slugify(section.heading);

                return (
                  <div
                    key={section.heading}
                    id={sectionId}
                    ref={registerSectionRef(sectionId)}
                    className="pd-section">
                    <h3 className="pd-section-heading">{section.heading}</h3>
                    <div className="pd-section-body">
                      {section.body.split('\n\n').map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {detail.reference && (
          <div className="pd-reference">
            <h2 className="pd-section-label">Reference</h2>
            <p className="pd-reference-text">{detail.reference}</p>
          </div>
        )}
      </div>

      <div className="pd-nav-spacer" />
    </div>
  );
};

export default ProjectDetail;
